import {
  ChoicesArticlesFilter,
  ChoicesComponentFilter,
  ChoicesProductGroupFilter,
} from "@generated"
import { getGraphQLClient } from "@lib/graphql-client"
import { useQuery } from "@tanstack/react-query"
import { BaseRouter } from "next/dist/next-server/lib/router/router"
import { useRouter } from "next/router"
import { getFixedMaterial } from "../helpers/get-fixed-material"
import { CONFIGURATOR_IDS_TO_SKIP } from "../helpers/ids-to-skip"
import { useBaseData } from "./use-base-data"

type ConfiguratorStatus = "loading" | "invalid" | "valid"

export function useConfiguratorData(component: ChoicesComponentFilter) {
  const router = useRouter()
  const baseData = useBaseData()
  const fixedMaterial = getFixedMaterial(router.query, baseData, component)

  const configuratorStatus: ConfiguratorStatus = getConfiguratorStatus(
    router.query
  )
  const filter = getFilter(router.query, component, fixedMaterial)

  usePreloadOtherComponents(filter)
  const query = useArticlesQuery(filter, configuratorStatus === "valid")
  const items = query.data?.choicesArticles?.items ?? []
  return {
    configuratorStatus,
    queryStatus: query.status,
    items: items.filter((item) => !CONFIGURATOR_IDS_TO_SKIP.includes(item.id)),
  }
}

function getFilter(
  query: BaseRouter["query"],
  component: ChoicesComponentFilter,
  fixedMaterial: ChoicesArticlesFilter["material"] | null
) {
  const filter: Filter = {
    component: component,
    productGroup: query.type as ChoicesProductGroupFilter,
    size: query.size
      ? (query.size as ChoicesArticlesFilter["size"])
      : undefined,
    style: query.style
      ? (query.style as ChoicesArticlesFilter["style"])
      : undefined,
    material: fixedMaterial ? fixedMaterial : undefined,
  }

  if (component === ChoicesComponentFilter.Base && query.substyle) {
    filter.substyle = query.substyle as ChoicesArticlesFilter["substyle"]
  }

  return filter
}

function usePreloadOtherComponents(filter: Filter, enabled?: boolean) {
  // Preload other components
  useArticlesQuery(
    { ...filter, component: ChoicesComponentFilter["Base"] },
    enabled
  )
  useArticlesQuery(
    { ...filter, component: ChoicesComponentFilter["Frame"] },
    enabled
  )
  useArticlesQuery(
    { ...filter, component: ChoicesComponentFilter["Stone"] },
    enabled
  )
}

function getConfiguratorStatus(
  query?: BaseRouter["query"]
): ConfiguratorStatus {
  if (!query) {
    return "loading"
  }
  // These three params are required, if they're not provided, display an empty state
  if (!query.style || !query.type || !query.substyle) {
    if (process.env.NODE_ENV === "development") {
      throw new Error(`Invalid query, missing param style, type or substyle`)
    }
    return "invalid"
  }
  if (
    query.style === "null" ||
    query.type === "null" ||
    query.substyle === "null" ||
    query.stone === "null" ||
    query.frame === "null" ||
    query.base === "null"
  ) {
    if (process.env.NODE_ENV === "development") {
      throw new Error(`Invalid query, one of the params is null`)
    }
    return "invalid"
  }
  if (query.type !== "ring" && query.type !== "earring") {
    if (process.env.NODE_ENV === "development") {
      throw new Error(`Invalid query, type should be ring or earring`)
    }
    return "invalid"
  }

  // A ring needs all three components + the size, an earring needs only a stone and base
  if (
    query.type === "ring" &&
    (!query.stone || !query.base || !query.frame || !query.size)
  ) {
    if (process.env.NODE_ENV === "development") {
      throw new Error(
        `Invalid query, selected type ${query.type}, but missing param stone, base, frame or size`
      )
    }
    return "invalid"
  }

  if (query.type === "earring" && (!query.stone || !query.base)) {
    if (process.env.NODE_ENV === "development") {
      throw new Error(
        `Invalid query, selected type ${query.type}, but missing param stone or base`
      )
    }
    return "invalid"
  }

  return "valid"
}

type Filter = Pick<
  ChoicesArticlesFilter,
  "component" | "productGroup" | "size" | "style" | "substyle" | "material"
>

const { sdk } = getGraphQLClient()
export function useArticlesQuery(filter?: Filter, enabled?: boolean) {
  const query = useQuery({
    queryKey: ["articles", filter],
    queryFn: () =>
      sdk.Articles({ filter, pagination: { limit: 200, skip: undefined } }),
    enabled,
  })

  return query
}
